import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useDonation } from '../../../../context/DonationContext';
import { donationService } from '../../../../services/api';

const DonationOptions = () => {
  const location = useLocation();
  const {
    selectedAmount,
    donationType,
    customAmount: contextCustomAmount,
    setDonationAmount,
    setDonationType,
    setCustomAmount: setContextCustomAmount
  } = useDonation();

  // 使用本地状态来处理金额输入，解决输入问题
  const [localCustomAmount, setLocalCustomAmount] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    countryCode: '+1',
    details: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [errors, setErrors] = useState({});
  const [selectedDonationType, setSelectedDonationType] = useState('MONEY');

  // 处理从其他页面跳转过来时的自动选择金额以及默认设置类型为MONEY
  useEffect(() => {
    // 默认设置捐赠类型为MONEY
    setDonationType('financial');
    
    if (location.state?.autoSelect) {
      const amount = location.state.preSelectedAmount;
      setDonationAmount(amount);
    }
  }, [location.state, setDonationAmount, setDonationType]);

  const getNextSaturday = () => {
    const today = new Date();
    const day = today.getDay();
    const daysUntilSaturday = (6 - day + 7) % 7;
    const nextSaturday = new Date(today);
    nextSaturday.setDate(today.getDate() + daysUntilSaturday);
    return nextSaturday.toLocaleDateString('en-US', { 
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const nextEventDate = getNextSaturday();

  const handleAmountChange = (e) => {
    // 只允许数字输入
    const value = e.target.value.replace(/[^0-9]/g, '');
    // 使用本地状态进行即时更新，解决输入问题
    setLocalCustomAmount(value);
    // 同时更新上下文状态
    setContextCustomAmount(value);
    setDonationAmount(null);
    
    // 清除金额错误
    if (errors.amount) {
      setErrors(prev => ({
        ...prev,
        amount: ''
      }));
    }
  };

  // 移除 handlePhoneNumberToggle 函数，因为我们不再使用勾选框

  const handleDonationTypeSelection = (type) => {
    setSelectedDonationType(type);
    if (type === 'MONEY') {
      setDonationType('financial');
    } else {
      setDonationType('goods');
    }
    setSubmitStatus({ type: '', message: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (selectedDonationType === 'MONEY') {
      if (!selectedAmount && !localCustomAmount) {
        newErrors.amount = 'Please select or enter an amount';
      }
    } else {
      if (!formData.details) {
        newErrors.details = 'Please provide donation details';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      if (selectedDonationType === 'MONEY') {
        await donationService.submitMonetaryDonation({
          amount: parseFloat(localCustomAmount || selectedAmount),
          email: formData.email,
          phoneNumber: formData.phoneNumber ? `${formData.countryCode} ${formData.phoneNumber}` : null,
          name: formData.name,
          paymentMethod: 'ZELLE'
        });
        
        setSubmitStatus({
          type: 'success',
          message: 'Thank you for your donation! Please check your email for Zelle payment instructions. Follow the steps in the email to complete your donation.'
        });
      } else {
        await donationService.submitGoodsDonation({
          donationType: selectedDonationType,
          details: formData.details,
          email: formData.email,
          phoneNumber: formData.phoneNumber ? `${formData.countryCode} ${formData.phoneNumber}` : null,
          name: formData.name
        });
        
        setSubmitStatus({
          type: 'success',
          message: 'Thank you for your donation offer! We will contact you soon to arrange the details.'
        });
      }

      setFormData({
        name: '',
        email: '',
        phoneNumber: '',
        countryCode: '+1',
        details: ''
      });
      setLocalCustomAmount('');
      setContextCustomAmount('');
      setDonationAmount(null);

    } catch (error) {
      console.error('Donation submission error:', error);
      setSubmitStatus({
        type: 'error',
        message: error.message || 'Failed to process donation. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="donation-options" className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2C2C2C] tracking-tight">
            Support Our Community Your Way
          </h2>
          <p className="text-lg sm:text-xl text-[#2C2C2C]/70">
            Choose how you'd like to make a difference
          </p>
          <p className="text-sm sm:text-base text-[#2C2C2C]/70">
            Join us for our next event on {nextEventDate}
          </p>
        </div>

        {/* 改为响应式布局：在手机上显示为2列，中等屏幕显示为3列，大屏幕显示为5列 */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          <button
            onClick={() => handleDonationTypeSelection('MONEY')}
            className={`p-2 md:p-3 rounded-lg transition-all duration-300 ${
              selectedDonationType === 'MONEY'
                ? 'bg-[#FFD200] shadow-lg scale-105 border-2 border-[#2C2C2C]'
                : 'bg-white hover:bg-[#FFD200]/10 border-2 border-[#2C2C2C]/10'
            }`}
          >
            <div className="text-sm md:text-lg font-bold text-[#2C2C2C] truncate">Money</div>
          </button>
          
          <button
            onClick={() => handleDonationTypeSelection('VENUE_SPACE')}
            className={`p-2 md:p-3 rounded-lg transition-all duration-300 ${
              selectedDonationType === 'VENUE_SPACE'
                ? 'bg-[#FFD200] shadow-lg scale-105 border-2 border-[#2C2C2C]'
                : 'bg-white hover:bg-[#FFD200]/10 border-2 border-[#2C2C2C]/10'
            }`}
          >
            <div className="text-sm md:text-lg font-bold text-[#2C2C2C] truncate">
              {/* 在手机尺寸下显示简短名称，在大屏幕下显示完整名称 */}
              <span className="hidden md:inline">Venue Space</span>
              <span className="inline md:hidden">Venue</span>
            </div>
          </button>
          
          <button
            onClick={() => handleDonationTypeSelection('GAMES')}
            className={`p-2 md:p-3 rounded-lg transition-all duration-300 ${
              selectedDonationType === 'GAMES'
                ? 'bg-[#FFD200] shadow-lg scale-105 border-2 border-[#2C2C2C]'
                : 'bg-white hover:bg-[#FFD200]/10 border-2 border-[#2C2C2C]/10'
            }`}
          >
            <div className="text-sm md:text-lg font-bold text-[#2C2C2C] truncate">Games</div>
          </button>
          
          <button
            onClick={() => handleDonationTypeSelection('GIFTS')}
            className={`p-2 md:p-3 rounded-lg transition-all duration-300 ${
              selectedDonationType === 'GIFTS'
                ? 'bg-[#FFD200] shadow-lg scale-105 border-2 border-[#2C2C2C]'
                : 'bg-white hover:bg-[#FFD200]/10 border-2 border-[#2C2C2C]/10'
            }`}
          >
            <div className="text-sm md:text-lg font-bold text-[#2C2C2C] truncate">Gifts</div>
          </button>
          
          <button
            onClick={() => handleDonationTypeSelection('OTHER')}
            className={`p-2 md:p-3 rounded-lg transition-all duration-300 ${
              selectedDonationType === 'OTHER'
                ? 'bg-[#FFD200] shadow-lg scale-105 border-2 border-[#2C2C2C]'
                : 'bg-white hover:bg-[#FFD200]/10 border-2 border-[#2C2C2C]/10'
            }`}
          >
            <div className="text-sm md:text-lg font-bold text-[#2C2C2C] truncate">Other</div>
          </button>
        </div>

        {selectedDonationType && (
          <form onSubmit={handleSubmit} className="space-y-3 bg-white p-4 rounded-lg border-2 border-[#2C2C2C]/10">
            {selectedDonationType === 'MONEY' ? (
              <>
                <div className="space-y-1">
                  <label className="block text-[#2C2C2C] font-medium">Donation Amount (USD)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#2C2C2C]">$</span>
                    <input
                      type="text"
                      value={localCustomAmount}
                      onChange={handleAmountChange}
                      placeholder="Enter amount in USD"
                      className={`w-full p-2 pl-7 rounded-lg border-2 ${
                        errors.amount ? 'border-red-500' : 'border-[#2C2C2C]/10'
                      } focus:border-[#FFD200] focus:ring-2 focus:ring-[#FFD200]/20 bg-white text-[#2C2C2C] placeholder:text-[#2C2C2C]/40 transition duration-200`}
                    />
                    {errors.amount && (
                      <p className="mt-1 text-sm text-red-500">{errors.amount}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="block text-[#2C2C2C] font-medium">Payment Method</label>
                  <div className="p-3 bg-[#F5F5F5] rounded-xl">
                    <div className="flex items-center space-x-2">
                      <span className="text-[#2C2C2C] font-medium">Zelle</span>
                      <span className="text-sm text-[#2C2C2C]/70">(Only payment method accepted)</span>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-blue-50 text-blue-800 border border-blue-200 rounded-xl text-sm">
                  After submitting this form, you will receive an email with detailed Zelle payment instructions. 
                  Please follow the steps in the email to complete your donation.
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1">
                  <label className="block text-[#2C2C2C] font-medium">Donation Details</label>
                  <textarea
                    name="details"
                    value={formData.details}
                    onChange={handleInputChange}
                    placeholder={`Please describe the ${selectedDonationType.toLowerCase().replace('_', ' ')} you would like to donate...`}
                    className={`w-full p-3 rounded-xl border-2 ${
                      errors.details ? 'border-red-500' : 'border-[#2C2C2C]/10'
                    } focus:border-[#FFD200] focus:ring-2 focus:ring-[#FFD200]/20 bg-white text-[#2C2C2C] placeholder:text-[#2C2C2C]/40 min-h-24 transition duration-200`}
                  />
                  {errors.details && (
                    <p className="mt-1 text-sm text-red-500">{errors.details}</p>
                  )}
                </div>
              </>
            )}

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="block text-[#2C2C2C] font-medium">Name</label>
                <span className="text-sm text-[#2C2C2C]/60">Optional</span>
              </div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your name"
                className="w-full p-3 rounded-xl border-2 border-[#2C2C2C]/10 focus:border-[#FFD200] focus:ring-2 focus:ring-[#FFD200]/20 bg-white text-[#2C2C2C] placeholder:text-[#2C2C2C]/40 transition duration-200"
              />
            </div>

            {/* 电话号码输入字段 */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label htmlFor="phoneNumber" className="block text-[#2C2C2C] font-medium">
                  Phone Number
                </label>
                <span className="text-sm text-[#2C2C2C]/60">Optional</span>
              </div>
              {/* 在小屏幕上将国家代码选择器和电话号码输入框改为堆叠布局 */}
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0">
                <select
                  name="countryCode"
                  value={formData.countryCode || "+1"}
                  onChange={handleInputChange}
                  className="sm:w-20 p-3 rounded-xl sm:rounded-l-xl sm:rounded-r-none border-2 sm:border-r-0 border-[#2C2C2C]/10 focus:border-[#FFD200] focus:ring-2 focus:ring-[#FFD200]/20 bg-white text-[#2C2C2C]"
                >
                  <option value="+1">+1</option>
                  <option value="+44">+44</option>
                  <option value="+86">+86</option>
                  <option value="+61">+61</option>
                  <option value="+33">+33</option>
                  <option value="+49">+49</option>
                  <option value="+81">+81</option>
                  <option value="+82">+82</option>
                  <option value="+91">+91</option>
                  <option value="+52">+52</option>
                  <option value="+55">+55</option>
                </select>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="(123) 456-7890"
                  className={`flex-1 p-3 rounded-xl sm:rounded-l-none sm:rounded-r-xl border-2 sm:border-l-0 ${
                    errors.phoneNumber ? 'border-red-500' : 'border-[#2C2C2C]/10'
                  } focus:border-[#FFD200] focus:ring-2 focus:ring-[#FFD200]/20 bg-white text-[#2C2C2C] placeholder:text-[#2C2C2C]/40 transition duration-200`}
                />
              </div>
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-red-500">{errors.phoneNumber}</p>
              )}
              <p className="text-xs text-[#2C2C2C]/60">
                We may contact you at this number to discuss your donation.
              </p>
            </div>

            <div className="space-y-1">
              <label className="block text-[#2C2C2C] font-medium">Contact Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                className={`w-full p-3 rounded-xl border-2 ${
                  errors.email ? 'border-red-500' : 'border-[#2C2C2C]/10'
                } focus:border-[#FFD200] focus:ring-2 focus:ring-[#FFD200]/20 bg-white text-[#2C2C2C] placeholder:text-[#2C2C2C]/40 transition duration-200`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            <p className="text-xs text-[#2C2C2C]/70">
              {selectedDonationType === 'MONEY'
                ? 'We will send you payment instructions via email. Please follow the steps in the email to complete your Zelle donation.'
                : 'We will contact you via email to discuss further collaboration details'}
            </p>

            <div className="space-y-2 pt-2">
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`
                    px-6 py-2.5
                    bg-[#FFD200] text-[#2C2C2C] 
                    rounded-xl font-bold text-base
                    transition-all duration-300 
                    ${isSubmitting ? 
                      'opacity-50 cursor-not-allowed' : 
                      'hover:bg-[#FFE566] transform hover:scale-105 active:scale-95'
                    }
                  `}
                >
                  {isSubmitting ? 'Processing...' : 'Submit'}
                </button>
              </div>
              
              {submitStatus.message && (
                <div className={`p-3 rounded-lg text-sm ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitStatus.message}
                </div>
              )}
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default DonationOptions;